'use client';

import demos from '@/generated/admin-demo-gifs.json';
import { localizedAdminDemos } from '@/lib/admin-demo';
import type { AdminDemoAsset } from '@/types/admin-demo';
import Image from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';

interface AdminDemoShowcaseProps {
  language: 'en' | 'zh';
  fallbackImage: string;
}

const demoAssets = demos as AdminDemoAsset[];

export default function AdminDemoShowcase({
  language,
  fallbackImage,
}: AdminDemoShowcaseProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [startIndex, setStartIndex] = useState(0);
  const [failedPosters, setFailedPosters] = useState<Set<string>>(new Set());
  const [failedVideos, setFailedVideos] = useState<Set<string>>(new Set());
  const [reduceMotion, setReduceMotion] = useState(false);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const showcaseRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const localizedAssets = useMemo(
    () => localizedAdminDemos(demoAssets, language),
    [language]
  );

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (showcaseRef.current) observer.observe(showcaseRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setActiveIndex(0);
    const random = new Uint32Array(1);
    window.crypto.getRandomValues(random);
    setStartIndex(
      localizedAssets.length ? random[0] % localizedAssets.length : 0
    );
  }, [localizedAssets]);

  const orderedAssets = useMemo(
    () => [
      ...localizedAssets.slice(startIndex),
      ...localizedAssets.slice(0, startIndex),
    ],
    [startIndex, localizedAssets]
  );
  const selectedIndex = activeIndex % Math.max(orderedAssets.length, 1);
  const active = orderedAssets[selectedIndex];
  const showPoster =
    reduceMotion || !active || failedVideos.has(active.videoSrc);

  useEffect(() => {
    if (
      reduceMotion ||
      paused ||
      !visible ||
      orderedAssets.length < 2 ||
      !active
    )
      return;
    const timeout = window.setTimeout(
      () => setActiveIndex(index => (index + 1) % orderedAssets.length),
      Math.max(4500, active.durationMs)
    );
    return () => window.clearTimeout(timeout);
  }, [active, orderedAssets.length, reduceMotion, paused, visible]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || showPoster) return;
    if (paused || !visible) video.pause();
    else
      void video.play().catch(error => {
        if (error.name === 'NotAllowedError' && videoRef.current === video)
          setPaused(true);
      });
  }, [active, paused, visible, showPoster]);

  const nextDemo = () =>
    setActiveIndex(index => (index + 1) % Math.max(orderedAssets.length, 1));
  const poster =
    active && !failedPosters.has(active.posterSrc)
      ? active.posterSrc
      : fallbackImage;
  const alt =
    active?.alt[language] ||
    (language === 'en'
      ? 'Omcean Booking administration interface'
      : 'Omcean Booking 管理介面');
  const showVideoFallback = () => {
    // MP4 is the only video format; playback failure uses the localized still.
    if (active)
      setFailedVideos(current => new Set(current).add(active.videoSrc));
  };

  return (
    <div
      ref={showcaseRef}
      className="w-[700px] max-w-[90vw]"
      data-testid="admin-demo-showcase"
      data-language={active?.language}
      data-demo-id={active?.id}
    >
      <div
        className="rounded-3xl border border-gray-700 bg-gradient-to-br from-gray-800 to-gray-950 p-2 shadow-2xl sm:p-3"
        data-testid="admin-demo-tablet"
      >
        <div
          className="relative aspect-video overflow-hidden rounded-2xl bg-gray-100 dark:bg-gray-900"
          data-testid="admin-demo-screen"
        >
          {showPoster ? (
            <Image
              src={poster}
              alt={alt}
              fill
              sizes="(max-width: 768px) 90vw, 700px"
              className="object-cover"
              onError={() =>
                active &&
                setFailedPosters(current =>
                  new Set(current).add(active.posterSrc)
                )
              }
            />
          ) : (
            <video
              ref={videoRef}
              key={active.videoSrc}
              src={active.videoSrc}
              className="absolute inset-0 h-full w-full object-cover"
              autoPlay={visible && !paused}
              loop
              muted
              playsInline
              preload="metadata"
              poster={active.posterSrc}
              aria-label={alt}
              onError={showVideoFallback}
            />
          )}

          {active && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 bg-gradient-to-t from-black/65 to-transparent px-3 pb-3 pt-9 text-white">
              <button
                type="button"
                data-testid="admin-demo-next"
                onClick={nextDemo}
                disabled={orderedAssets.length < 2}
                aria-label={`${language === 'en' ? 'Next demo' : '下一個示範'}: ${active.label[language]}`}
                className="pointer-events-auto flex min-h-9 items-center gap-2 rounded-full bg-black/45 px-3 py-2 text-left text-xs font-semibold backdrop-blur-sm transition-colors hover:bg-black/65 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {active.label[language]}
                {orderedAssets.length > 1 && <span aria-hidden="true">→</span>}
              </button>
              {!reduceMotion && (
                <button
                  type="button"
                  data-testid="admin-demo-pause"
                  onClick={() => setPaused(value => !value)}
                  aria-label={
                    language === 'en'
                      ? paused
                        ? 'Play demo'
                        : 'Pause demo'
                      : paused
                        ? '播放示範'
                        : '暫停示範'
                  }
                  aria-pressed={paused}
                  className="pointer-events-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black/45 text-xs backdrop-blur-sm hover:bg-black/65 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                >
                  <span aria-hidden="true">{paused ? '▶' : 'Ⅱ'}</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {orderedAssets.length > 1 && (
        <div
          className="mt-3 flex flex-wrap items-center justify-center gap-0.5"
          role="group"
          aria-label={
            language === 'en' ? 'Choose an admin demo' : '選擇管理示範'
          }
          data-testid="admin-demo-indicators"
        >
          {orderedAssets.map((asset, index) => (
            <button
              type="button"
              key={asset.id}
              onClick={() => setActiveIndex(index)}
              aria-label={asset.label[language]}
              aria-pressed={index === selectedIndex}
              className="flex h-9 w-7 items-center justify-center rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foreground)]"
            >
              <span
                className={`h-1.5 rounded-full bg-[var(--color-foreground)] transition-all ${index === selectedIndex ? 'w-5 opacity-90' : 'w-1.5 opacity-30'}`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
